"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import { useCallback, useEffect, useRef, useState } from "react";

const uiLog = createLogAdapter("hooks/useUserInfo.ts");

// 用户信息接口定义
interface UserInfo {
	principal: string;
	username: string;
	bio: string | null;
	image: string | null;
	referrer: string;
	admin: number;
	ref_code: string;
	profit: number | null;
	total_asset_value: number | null;
	referral_earnings: number;
	referral_count: number;
	access_allowed: boolean;
	beta_access_codes: string;
	btc_deposit_address: string;
	btc_wallet_address: string;
	blife_id: string;
	created_at: string;
	rune_deposit_address: string;
	invite_code?: string;
}

// API响应结构（兼容不同的响应格式）
interface ApiResponse {
	message?: string;
	code?: number;
	data?: UserInfo;
	// 如果直接返回用户信息
	principal?: string;
	username?: string;
	[key: string]: any;
}

interface UseUserInfoParams {
	userId?: string;
	principal?: string;
	enabled?: boolean; // 是否启用自动获取
}

interface UseUserInfoResult {
	userInfo: UserInfo | null;
	isLoading: boolean;
	error: string | null;
	status: "idle" | "loading" | "success" | "not_found" | "error";
	refetch: () => void;
}

// 全局缓存
const userInfoCache = new Map<string, { data: UserInfo; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存
const MAX_RETRY_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 800;

function isAbortError(error: unknown) {
	return error instanceof DOMException && error.name === "AbortError";
}

function isRetryableStatus(status: number) {
	return status === 408 || status === 429 || status >= 500;
}

function getRetryDelay(attempt: number) {
	return Math.min(RETRY_BASE_DELAY_MS * 2 ** attempt, 3000);
}

async function waitForRetry(ms: number) {
	await new Promise((resolve) => setTimeout(resolve, ms));
}

export function useUserInfo({
	userId,
	principal,
	enabled = true,
}: UseUserInfoParams): UseUserInfoResult {
	const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "not_found" | "error"
	>("idle");
	const abortControllerRef = useRef<AbortController | null>(null);
	const requestIdRef = useRef(0);

	// 生成缓存键
	const getCacheKey = useCallback((id?: string, principal?: string) => {
		if (id) return `user_info_id_${id}`;
		if (principal) return `user_info_principal_${principal}`;
		return null;
	}, []);

	const fetchUserInfo = useCallback(
		async (
			id?: string,
			principalParam?: string,
			options?: { force?: boolean },
		) => {
			if (!id && !principalParam) return;

			const cacheKey = getCacheKey(id, principalParam);
			if (!cacheKey) return;

			abortControllerRef.current?.abort();
			const abortController = new AbortController();
			abortControllerRef.current = abortController;
			const requestId = ++requestIdRef.current;

			try {
				// 检查缓存
				const cached = userInfoCache.get(cacheKey);
				const now = Date.now();

				if (
					!options?.force &&
					cached &&
					now - cached.timestamp < CACHE_DURATION
				) {
					setUserInfo(cached.data);
					setStatus("success");
					setError(null);
					return;
				}

				setStatus("loading");
				setError(null);
				setUserInfo(null);

				let apiUrl: string;

				// 根据参数类型构造不同的API URL
				if (id) {
					// 通过用户ID获取，调用 odin.fun API
					apiUrl = `https://api.odin.fun/v1/user/${encodeURIComponent(id)}`;
				} else if (principalParam) {
					// 通过principal获取，调用 tyche.run API （暂时保持原逻辑，后续可以统一）
					// 这里我们也改为直接调用 api.odin.fun，但需要确认具体的端点
					// 先保持兼容，如果有问题再调整
					apiUrl = `https://api.odin.fun/v1/user/${encodeURIComponent(principalParam)}`;
				} else {
					throw new Error("Invalid parameters");
				}

				uiLog.info(`Fetching user info from: ${apiUrl}`);

				for (let attempt = 0; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
					try {
						const response = await fetch(apiUrl, {
							method: "GET",
							headers: {
								"Content-Type": "application/json",
								// 可以根据需要添加其他headers
							},
							signal: abortController.signal,
						});

						if (requestId !== requestIdRef.current) {
							return;
						}

						if (response.status === 404) {
							setUserInfo(null);
							setStatus("not_found");
							setError(null);
							return;
						}

						if (!response.ok) {
							if (
								isRetryableStatus(response.status) &&
								attempt < MAX_RETRY_ATTEMPTS
							) {
								const retryDelay = getRetryDelay(attempt);
								uiLog.warn(
									`User info request failed with ${response.status}, retrying in ${retryDelay}ms`,
								);
								await waitForRetry(retryDelay);
								continue;
							}

							throw new Error(
								`API responded with status: ${response.status} ${response.statusText}`,
							);
						}

						const data: ApiResponse = await response.json();

						if (requestId !== requestIdRef.current) {
							return;
						}

						// 处理不同的响应格式
						let userInfoData: UserInfo;

						if (data.data) {
							// 如果响应包含data字段
							userInfoData = data.data;
						} else if (data.principal) {
							// 如果直接返回用户信息
							userInfoData = data as UserInfo;
						} else {
							throw new Error("Invalid response format");
						}

						// 更新缓存
						userInfoCache.set(cacheKey, {
							data: userInfoData,
							timestamp: now,
						});

						setUserInfo(userInfoData);
						setStatus("success");
						setError(null);
						uiLog.info("User info fetched successfully:", userInfoData);
						return;
					} catch (err) {
						if (isAbortError(err)) {
							return;
						}

						const errorMessage =
							err instanceof Error ? err.message : "Failed to fetch user info";
						const canRetry = attempt < MAX_RETRY_ATTEMPTS;

						if (canRetry) {
							const retryDelay = getRetryDelay(attempt);
							uiLog.warn(
								`User info request failed (${errorMessage}), retrying in ${retryDelay}ms`,
							);
							await waitForRetry(retryDelay);
							continue;
						}

						uiLog.error("Error fetching user info:", err);
						if (requestId !== requestIdRef.current) {
							return;
						}

						setError(errorMessage);
						setStatus("error");
						setUserInfo(null);
						return;
					}
				}
			} catch (err) {
				if (isAbortError(err)) {
					return;
				}

				const errorMessage =
					err instanceof Error ? err.message : "Failed to fetch user info";
				uiLog.error("Unexpected user info error:", err);
				if (requestId !== requestIdRef.current) {
					return;
				}

				setError(errorMessage);
				setStatus("error");
				setUserInfo(null);
			}
		},
		[getCacheKey],
	);

	const refetch = useCallback(() => {
		if (userId || principal) {
			fetchUserInfo(userId, principal, { force: true });
		}
	}, [userId, principal, fetchUserInfo]);

	useEffect(() => {
		if (!enabled || (!userId && !principal)) {
			setUserInfo(null);
			setError(null);
			setStatus("idle");
			return;
		}

		void fetchUserInfo(userId, principal);

		return () => {
			abortControllerRef.current?.abort();
		};
	}, [userId, principal, enabled, fetchUserInfo]);

	return {
		userInfo,
		isLoading: status === "loading",
		error,
		status,
		refetch,
	};
}

// 便捷方法：根据用户ID获取用户信息
export function useUserInfoById(userId: string | null, enabled = true) {
	return useUserInfo({ userId: userId || undefined, enabled });
}

// 便捷方法：根据principal获取用户信息
export function useUserInfoByPrincipal(
	principal: string | null,
	enabled = true,
) {
	return useUserInfo({ principal: principal || undefined, enabled });
}
